/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { KeyboardEvent } from "react";
import { When } from "react-if";
import styled from "styled-components";
import { Description, Input, theme, FlexContainer } from "@nordcloud/gnui";
import { useCostHook } from "./hooks";

const CostContainer = styled.div<{
  size: "m" | "s";
}>`
  margin-right: ${theme.spacing.spacing02};
  width: ${({ size }) => (size === "s" ? "9rem" : "14rem")};
`;

type Props = {
  from?: number;
  to?: number;
  onChangeFrom: (input: string) => void;
  onChangeTo: (input: string) => void;
  size?: "m" | "s";
};

export function NumberRangeInput({
  from,
  to,
  onChangeFrom,
  onChangeTo,
  size = "s",
}: Props) {
  const { isCostToInvalid, isCostFromNegative, isCostToNegative } = useCostHook(
    from,
    to
  );

  return (
    <FlexContainer direction="column">
      <FlexContainer>
        <CostContainer size={size}>
          <Input
            type="number"
            min="0"
            placeholder="From"
            status={isCostFromNegative ? "danger" : undefined}
            value={from ?? ""}
            showClearButton={from !== undefined}
            onChange={(event) => onChangeFrom(event.target.value)}
            onClear={() => onChangeFrom("")}
            onKeyDown={handleKeyPress}
          />
        </CostContainer>
        <CostContainer size={size}>
          <Input
            type="number"
            min="0"
            placeholder="To"
            status={isCostToInvalid || isCostToNegative ? "danger" : undefined}
            value={to ?? ""}
            showClearButton={to !== undefined}
            onChange={(event) => onChangeTo(event.target.value)}
            onClear={() => onChangeTo("")}
            onKeyDown={handleKeyPress}
          />
        </CostContainer>
      </FlexContainer>
      <When condition={isCostToInvalid}>
        <Description>
          Value &quot;To&quot; must be bigger than &quot;From&quot;.
        </Description>
      </When>
      <When condition={isCostFromNegative || isCostToNegative}>
        <Description>Value must be bigger than 0.</Description>
      </When>
    </FlexContainer>
  );
}

const exceptSymbols = ["e", "E"];

const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
  if (exceptSymbols.includes(event.key)) {
    return event.preventDefault();
  }
};
